import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import databaseCleanup from '../utils/databaseCleanup';

const AdminCleanupPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [logs, setLogs] = useState([]);
  const [integrityCheck, setIntegrityCheck] = useState(null);

  const ADMIN_EMAIL = 'petre-alexandru.dumitrescu0721@stud.umfcd.ro';

  // Security check
  useEffect(() => {
    if (!user || user.isGuest || user.email !== ADMIN_EMAIL) {
      navigate('/calculator', { replace: true });
    }
  }, [user, navigate]);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  };

  const runIntegrityCheck = async () => {
    addLog('Starting integrity check...', 'info');
    try {
      const check = await databaseCleanup.verifyIntegrity();
      setIntegrityCheck(check);
      addLog(`Integrity check completed. Status: ${check.passed ? 'CLEAN' : 'NEEDS CLEANUP'}`, check.passed ? 'success' : 'warning');
    } catch (error) {
      addLog(`Integrity check failed: ${error.message}`, 'error');
    }
  };

  const runCleanup = async () => {
    setIsRunning(true);
    setResults(null);
    setLogs([]);
    
    addLog('Starting database cleanup...', 'info');
    
    try {
      const cleanupResults = await databaseCleanup.runFullCleanup();
      setResults(cleanupResults);
      addLog('Cleanup completed successfully!', 'success');
      
      // Run post-cleanup integrity check
      await runIntegrityCheck();
      
    } catch (error) {
      addLog(`Cleanup failed: ${error.message}`, 'error');
      
      // Check if it's an index error
      if (error.message.includes('index') || error.message.includes('Index')) {
        addLog('Index missing! Please create required indexes first.', 'warning');
        addLog('Run: firebase deploy --only firestore:indexes', 'info');
        addLog('Or visit: https://console.firebase.google.com/project/calculator-medie-umfcd/firestore/indexes', 'info');
      }
    } finally {
      setIsRunning(false);
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'success': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  // Don't render if not admin
  if (!user || user.isGuest || user.email !== ADMIN_EMAIL) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-xl">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/admin/stats')}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back to Admin Stats</span>
            </button>

            <div className="flex items-center space-x-4">
              <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                🔧 Admin Panel
              </div>
              <button
                onClick={logout}
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8">
          {/* Title */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🔧</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Database Cleanup Utility</h1>
            <p className="text-gray-600">Remove duplicate entries and ensure ranking accuracy</p>
          </div>
          
          {/* Controls */}
          <div className="flex flex-wrap gap-4 mb-8 justify-center">
            <button
              onClick={runIntegrityCheck}
              disabled={isRunning}
              className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 transition-all duration-200 flex items-center space-x-2"
            >
              <span>🔍</span>
              <span>Check Integrity</span>
            </button>
            
            <button
              onClick={runCleanup}
              disabled={isRunning}
              className="px-6 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 disabled:opacity-50 transition-all duration-200 flex items-center space-x-2"
            >
              <span>{isRunning ? '⏳' : '🗑️'}</span>
              <span>{isRunning ? 'Running Cleanup...' : 'Run Cleanup'}</span>
            </button>
          </div>

          {/* Integrity Check Results */}
          {integrityCheck && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <span className="mr-2">📊</span>
                Integrity Check Results
              </h2>
              <div className={`p-6 rounded-2xl ${integrityCheck.passed ? 'bg-green-50 border-2 border-green-200' : 'bg-yellow-50 border-2 border-yellow-200'}`}>
                <p className={`font-bold text-lg mb-4 ${integrityCheck.passed ? 'text-green-800' : 'text-yellow-800'}`}>
                  Status: {integrityCheck.passed ? '✅ DATABASE CLEAN' : '⚠️ CLEANUP NEEDED'}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white/80 rounded-xl p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Latest Averages</h4>
                    <div className="space-y-1 text-sm">
                      <p>Total: <span className="font-bold">{integrityCheck.checks.latestAverages?.totalEntries || 0}</span></p>
                      <p>Unique: <span className="font-bold text-green-600">{integrityCheck.checks.latestAverages?.uniqueKeys || 0}</span></p>
                      <p>Duplicates: <span className="font-bold text-red-600">{integrityCheck.checks.latestAverages?.duplicates || 0}</span></p>
                    </div>
                  </div>
                  
                  <div className="bg-white/80 rounded-xl p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Calculations</h4>
                    <div className="space-y-1 text-sm">
                      <p>Total: <span className="font-bold">{integrityCheck.checks.calculations?.totalEntries || 0}</span></p>
                      <p>Unique: <span className="font-bold text-green-600">{integrityCheck.checks.calculations?.uniqueKeys || 0}</span></p>
                      <p>Duplicates: <span className="font-bold text-red-600">{integrityCheck.checks.calculations?.duplicates || 0}</span></p>
                    </div>
                  </div>
                  
                  <div className="bg-white/80 rounded-xl p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Subject Grades</h4>
                    <div className="space-y-1 text-sm">
                      <p>Total: <span className="font-bold">{integrityCheck.checks.subjectGrades?.totalEntries || 0}</span></p>
                      <p>Unique: <span className="font-bold text-green-600">{integrityCheck.checks.subjectGrades?.uniqueKeys || 0}</span></p>
                      <p>Duplicates: <span className="font-bold text-red-600">{integrityCheck.checks.subjectGrades?.duplicates || 0}</span></p>
                    </div>
                  </div>

                  <div className="bg-white/80 rounded-xl p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Subject Analytics</h4>
                    <div className="space-y-1 text-sm">
                      <p>Total: <span className="font-bold">{integrityCheck.checks.subjectAnalytics?.totalEntries || 0}</span></p>
                      <p>Unique: <span className="font-bold text-green-600">{integrityCheck.checks.subjectAnalytics?.uniqueKeys || 0}</span></p>
                      <p>Duplicates: <span className="font-bold text-red-600">{integrityCheck.checks.subjectAnalytics?.duplicates || 0}</span></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cleanup Results */}
          {results && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <span className="mr-2">✅</span>
                Cleanup Results
              </h2>
              <div className="bg-green-50 border-2 border-green-200 p-6 rounded-2xl">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
                  <div className="bg-white/80 rounded-xl p-4 text-center">
                    <h4 className="font-semibold text-green-800 mb-2">Latest Averages</h4>
                    <p className="text-2xl font-bold text-red-600">{results.latestAverages?.duplicatesDeleted || 0}</p>
                    <p className="text-sm text-gray-600">Deleted</p>
                    <p className="text-lg font-semibold text-green-600 mt-1">{results.latestAverages?.uniqueEntries || 0}</p>
                    <p className="text-sm text-gray-600">Remaining</p>
                  </div>
                  
                  <div className="bg-white/80 rounded-xl p-4 text-center">
                    <h4 className="font-semibold text-green-800 mb-2">Calculations</h4>
                    <p className="text-2xl font-bold text-red-600">{results.calculations?.duplicatesDeleted || 0}</p>
                    <p className="text-sm text-gray-600">Deleted</p>
                    <p className="text-lg font-semibold text-green-600 mt-1">{results.calculations?.uniqueEntries || 0}</p>
                    <p className="text-sm text-gray-600">Remaining</p>
                  </div>
                  
                  <div className="bg-white/80 rounded-xl p-4 text-center">
                    <h4 className="font-semibold text-green-800 mb-2">Subject Grades</h4>
                    <p className="text-2xl font-bold text-red-600">{results.subjectGrades?.duplicatesDeleted || 0}</p>
                    <p className="text-sm text-gray-600">Deleted</p>
                    <p className="text-lg font-semibold text-green-600 mt-1">{results.subjectGrades?.uniqueEntries || 0}</p>
                    <p className="text-sm text-gray-600">Remaining</p>
                  </div>

                  <div className="bg-white/80 rounded-xl p-4 text-center">
                    <h4 className="font-semibold text-green-800 mb-2">Subject Analytics</h4>
                    <p className="text-2xl font-bold text-red-600">{results.subjectAnalytics?.duplicatesDeleted || 0}</p>
                    <p className="text-sm text-gray-600">Deleted</p>
                    <p className="text-lg font-semibold text-green-600 mt-1">{results.subjectAnalytics?.uniqueEntries || 0}</p>
                    <p className="text-sm text-gray-600">Remaining</p>
                  </div>
                </div>
                
                <div className="text-center bg-green-100 rounded-xl p-4">
                  <p className="text-xl font-bold text-green-800">
                    🗑️ Total entries deleted: {
                      (results.latestAverages?.duplicatesDeleted || 0) + 
                      (results.calculations?.duplicatesDeleted || 0) + 
                      (results.subjectGrades?.duplicatesDeleted || 0) +
                      (results.subjectAnalytics?.duplicatesDeleted || 0)
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Logs */}
          {logs.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <span className="mr-2">📝</span>
                Operation Log
              </h2>
              <div className="bg-gray-900 border-2 border-gray-700 rounded-2xl p-4 h-64 overflow-y-auto font-mono text-sm">
                {logs.map((log, index) => (
                  <div key={index} className="mb-1">
                    <span className="text-gray-400">[{log.timestamp}]</span>
                    <span className={`ml-2 ${getLogColor(log.type)}`}>{log.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Enhanced Warning with Index Info */}
          <div className="bg-red-50 border-2 border-red-200 p-6 rounded-2xl">
            <h3 className="font-bold text-red-800 mb-3 flex items-center">
              <span className="mr-2">⚠️</span>
              Important Security Notes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul className="text-sm text-red-700 space-y-2">
                <li className="flex items-start">
                  <span className="mr-2">🔒</span>
                  <span>This page is restricted to admin access only</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">💾</span>
                  <span>Always backup database before cleanup</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🔍</span>
                  <span>Run integrity check first to assess situation</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">📊</span>
                  <span>Ensure Firestore indexes are deployed first</span>
                </li>
              </ul>
              <ul className="text-sm text-red-700 space-y-2">
                <li className="flex items-start">
                  <span className="mr-2">🗑️</span>
                  <span>Operation permanently deletes duplicate entries</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🎯</span>
                  <span>Ensures accurate rankings (one entry per user/faculty/year)</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">📈</span>
                  <span>Improves database performance and data integrity</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">⚡</span>
                  <span>Run: firebase deploy --only firestore:indexes</span>
                </li>
              </ul>
            </div>
            
            {/* Index Deployment Instructions */}
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <h4 className="font-bold text-yellow-800 mb-2 flex items-center">
                <span className="mr-2">🔧</span>
                Index Deployment Required
              </h4>
              <div className="text-sm text-yellow-700 space-y-1">
                <p>Before running cleanup, ensure indexes are deployed:</p>
                <code className="block bg-yellow-100 p-2 rounded mt-2 font-mono text-xs">
                  firebase deploy --only firestore:indexes
                </code>
                <p className="text-xs mt-2">
                  Or create manually: <a 
                    href="https://console.firebase.google.com/project/calculator-medie-umfcd/firestore/indexes" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    Firebase Console
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCleanupPage;
